import { ScheduleChangeOptions } from "@common/schemas/domains/accounting/scheduleLifecycle.schema";
import { ScheduleChangeJournalEntryMode } from "@common/types/domains/accounting/schedule";
import { formatDateOnly } from "@helpers/date";
import { joinWithAnd } from "@helpers/joinWithAnd";

export interface ScheduleChangeOptionCopy {
    mode: ScheduleChangeJournalEntryMode;
    title: string;
    description: string;
    isDisabled: boolean;
    /** Why reverse is off, naming the months to unlock. */
    lockedHint: string | null;
}

/**
 * How a change treats what is already earned (memo §7, Figma "Change").
 * With nothing earned there is nothing to choose and the dialog shows no
 * options. Reverse stays visible but disabled when a month is locked, so the
 * user learns that unlocking is the way back to it; remove is simply hidden.
 */
export function buildScheduleChangeOptionsCopy(
    changeOptions: ScheduleChangeOptions | null,
): ScheduleChangeOptionCopy[] {
    if (!changeOptions || changeOptions.earnedJournalEntryCount === 0) {
        return [];
    }
    const available = new Set(changeOptions.availableJournalEntryModes);
    const firstOpenMonth = formatDateOnly(
        changeOptions.catchUpDate,
        "MMMM yyyy",
    );
    const isReverseAvailable = available.has(
        ScheduleChangeJournalEntryMode.reverse,
    );
    const options: ScheduleChangeOptionCopy[] = [
        {
            mode: ScheduleChangeJournalEntryMode.catchUp,
            title: "Catch up",
            description: `Keep past periods as they are and post one adjustment in ${firstOpenMonth}.`,
            isDisabled: false,
            lockedHint: null,
        },
        {
            mode: ScheduleChangeJournalEntryMode.reverse,
            title: "Reverse",
            description:
                "Post reversals for each affected month and repost the new schedule. Nothing is deleted.",
            isDisabled: !isReverseAvailable,
            lockedHint: isReverseAvailable
                ? null
                : `Unlock ${joinWithAnd(changeOptions.lockedMonths)} to reverse each month.`,
        },
    ];
    if (available.has(ScheduleChangeJournalEntryMode.remove)) {
        options.push({
            mode: ScheduleChangeJournalEntryMode.remove,
            title: "Remove",
            description:
                "Delete the affected entries and repost the new schedule. The change is logged in Activity.",
            isDisabled: false,
            lockedHint: null,
        });
    }
    return options;
}
